import { useEffect, useState, useCallback, useMemo } from "react";
import api, { dkk, num, parseAmount } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, PackageX } from "lucide-react";

const REASONS = { udløbet: "Udløbet", kasseret: "Kasseret", skade: "Skade", andet: "Andet" };
const REASON_CLS = {
  udløbet: "bg-amber-100 text-amber-700", kasseret: "bg-red-100 text-red-700",
  skade: "bg-orange-100 text-orange-700", andet: "bg-slate-100 text-slate-600",
};
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtDate = (s) => { if (!s) return ""; const [y, m, d] = s.split("-"); return `${parseInt(d, 10)}/${parseInt(m, 10)}-${y}`; };
const MONTHS = ["Januar", "Februar", "Marts", "April", "Maj", "Juni", "Juli", "August", "September", "Oktober", "November", "December"];
const empty = () => ({ date: iso(new Date()), product: "", qty: "1", reason: "udløbet", unit_cost: "", store: "falster" });

export function WasteLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty());
  const [month, setMonth] = useState(iso(new Date()).slice(0, 7));

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/waste"); setRows(data); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const liveTotal = (parseAmount(form.qty) || 0) * (parseAmount(form.unit_cost) || 0);

  const submit = async () => {
    if (!form.product.trim()) { toast.error("Skriv en vare"); return; }
    const body = { date: form.date, product: form.product.trim(), qty: parseAmount(form.qty) || 0, reason: form.reason, unit_cost: parseAmount(form.unit_cost) || 0, store: form.store };
    try { await api.post("/waste", body); toast.success("Registreret"); setForm((f) => ({ ...empty(), date: f.date, store: f.store })); await load(); }
    catch { toast.error("Kunne ikke gemme"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Slet denne registrering?")) return;
    try { await api.delete(`/waste/${id}`); setRows((r) => r.filter((x) => x.id !== id)); }
    catch { toast.error("Kunne ikke slette"); }
  };

  const months = useMemo(() => Array.from(new Set(rows.map((r) => (r.date || "").slice(0, 7)).filter(Boolean))).sort().reverse(), [rows]);
  const inMonth = useMemo(() => rows.filter((r) => (r.date || "").startsWith(month)), [rows, month]);
  const monthTotal = inMonth.reduce((s, r) => s + (r.total || 0), 0);
  const byReason = useMemo(() => {
    const acc = {};
    inMonth.forEach((r) => { acc[r.reason] = (acc[r.reason] || 0) + (r.total || 0); });
    return Object.entries(acc).map(([k, v]) => ({ reason: k, total: v })).sort((a, b) => b.total - a.total);
  }, [inMonth]);
  const maxReason = Math.max(...byReason.map((r) => r.total), 1);
  const monthLabel = (m) => { const [y, mm] = m.split("-"); return `${MONTHS[+mm - 1]} ${y}`; };

  return (
    <div className="space-y-4" data-testid="waste-log">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <PackageX className="w-4 h-4" />
        <span>Svind &amp; spild · registrér kasserede/udløbne varer og se hvad det koster</span>
      </div>

      {/* Form */}
      <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 sm:p-5" data-testid="waste-editor">
        <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500 mb-4">Registrér svind</h3>
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1.5 block">Dato</label>
            <Input type="date" value={form.date} onChange={(e) => setField("date", e.target.value)} className="h-10 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800" data-testid="waste-date" />
          </div>
          <div className="col-span-2 lg:col-span-1">
            <label className="text-xs font-medium text-slate-500 mb-1.5 block">Vare</label>
            <Input value={form.product} onChange={(e) => setField("product", e.target.value)} placeholder="fx Rugbrød" className="h-10 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800" data-testid="waste-product" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1.5 block">Antal</label>
            <Input value={form.qty} onChange={(e) => setField("qty", e.target.value)} inputMode="decimal" className="h-10 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800 tabular-nums" data-testid="waste-qty" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1.5 block">Årsag</label>
            <Select value={form.reason} onValueChange={(v) => setField("reason", v)}>
              <SelectTrigger className="h-10 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="waste-reason"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-[#e2e8f0] text-slate-700">
                {Object.entries(REASONS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1.5 block">Kostpris/stk</label>
            <div className="relative">
              <Input value={form.unit_cost} onChange={(e) => setField("unit_cost", e.target.value)} inputMode="decimal" placeholder="0,00" className="h-10 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-800 pr-8 tabular-nums" data-testid="waste-unitcost" />
              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400">kr.</span>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 mb-1.5 block">Butik</label>
            <Select value={form.store} onValueChange={(v) => setField("store", v)}>
              <SelectTrigger className="h-10 rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="waste-store"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-[#e2e8f0] text-slate-700">
                <SelectItem value="falster">Falster</SelectItem>
                <SelectItem value="ringsted">Ringsted</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-slate-500">Tab: <span className="font-bold text-slate-900 tabular-nums">{dkk(liveTotal)}</span></span>
          <Button onClick={submit} className="h-10 rounded-lg bg-[#38BDF8] hover:bg-sky-400 text-[#0b1120] gap-1.5 font-medium" data-testid="waste-add-btn"><Plus className="w-4 h-4" /> Registrér</Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs uppercase tracking-wider text-slate-500">Svind i perioden</span>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="h-7 w-auto rounded-md bg-[#f8fafc] border-[#e2e8f0] text-slate-600 text-xs gap-1" data-testid="waste-month"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-[#e2e8f0] text-slate-700">
                {(months.length ? months : [month]).map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <p className="font-heading text-3xl font-bold text-red-600 tabular-nums" data-testid="waste-month-total">{dkk(monthTotal)}</p>
          <p className="text-xs text-slate-400 mt-1">{inMonth.length} registreringer</p>
        </div>
        <div className="lg:col-span-2 rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5">
          <span className="text-xs uppercase tracking-wider text-slate-500">Top-årsager ({monthLabel(month)})</span>
          {byReason.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">Intet svind registreret i perioden 🎉</p>
          ) : (
            <ul className="space-y-2.5 mt-3">
              {byReason.map((r) => (
                <li key={r.reason} data-testid={`waste-reason-${r.reason}`}>
                  <div className="flex justify-between text-sm mb-1"><span className="text-slate-700">{REASONS[r.reason] || r.reason}</span><span className="font-semibold tabular-nums text-slate-800">{dkk(r.total)}</span></div>
                  <div className="h-1.5 rounded bg-[#f1f5f9] overflow-hidden"><div className="h-full rounded bg-red-400" style={{ width: `${(r.total / maxReason) * 100}%` }} /></div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-5">
        {loading ? (
          <div className="h-24 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : inMonth.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center" data-testid="waste-empty">Ingen registreringer i {monthLabel(month)}.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-slate-700" data-testid="waste-table">
              <thead className="text-slate-500 border-b border-[#e2e8f0] text-xs uppercase tracking-wider">
                <tr><th className="text-left font-medium py-2 pr-2">Dato</th><th className="text-left font-medium py-2 pr-2">Vare</th><th className="text-left font-medium py-2 pr-2">Årsag</th><th className="text-left font-medium py-2 pr-2">Butik</th><th className="text-right font-medium py-2 pr-2">Antal</th><th className="text-right font-medium py-2 pr-2">Kostpris</th><th className="text-right font-medium py-2 pr-2">Tab</th><th className="w-8"></th></tr>
              </thead>
              <tbody>
                {inMonth.map((r) => (
                  <tr key={r.id} className="border-b border-[#f1f5f9] hover:bg-[#f8fafc]" data-testid={`waste-row-${r.id}`}>
                    <td className="py-2.5 pr-2 tabular-nums text-slate-600">{fmtDate(r.date)}</td>
                    <td className="py-2.5 pr-2 font-medium text-slate-800">{r.product}</td>
                    <td className="py-2.5 pr-2"><span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${REASON_CLS[r.reason] || REASON_CLS.andet}`}>{REASONS[r.reason] || r.reason}</span></td>
                    <td className="py-2.5 pr-2 capitalize text-slate-500">{r.store}</td>
                    <td className="py-2.5 pr-2 text-right tabular-nums">{num(r.qty)}</td>
                    <td className="py-2.5 pr-2 text-right tabular-nums text-slate-500">{dkk(r.unit_cost)}</td>
                    <td className="py-2.5 pr-2 text-right tabular-nums font-semibold text-red-600">{dkk(r.total)}</td>
                    <td className="py-2.5 text-right"><button onClick={() => remove(r.id)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`waste-del-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default WasteLog;
